/**
 * P3-27: TradeTab —— Trade tab 迁移（race 面板 + TradeButton 先落地）。
 *
 * 迁移自 `com.nuclearunicorn.game.ui.tab.Diplomacy`
 *（js/diplomacy.js:1553-1829）。
 *
 * 当前范围：
 * - 头部 trade effectiveness + sharkRelationsMerchants 加成；
 * - expand all / collapse all；
 * - 每个 unlocked race 的 RacePanel（含 buys/sells/TradeButton）；
 * - Explore 按钮占位（P3-28 替换为真实组件）。
 */
import { useCallback, useMemo, useState } from "react";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { RacePanel } from "./RacePanel";
import { SendExplorersButton } from "./SendExplorersButton";
import type { RaceLike } from "./useTradeModel";

interface TradeSnapshot {
  effectiveness: string;
  sharkMerchantsBonus: string;
  showSharkMerchants: boolean;
  races: RaceLike[];
}

function computeSnapshot(game: GamePage | null): TradeSnapshot | null {
  if (!game) {
    return null;
  }
  const dip = game.diplomacy as {
    getTradeRatio: () => number;
    races: RaceLike[];
  };
  const challenges = game.challenges as {
    getChallenge: (name: string) => { getTradeBonusEffect: (game: GamePage) => number };
  };
  const pacifismBonus = challenges.getChallenge("pacifism").getTradeBonusEffect(game);
  const effectiveness = (dip.getTradeRatio() + pacifismBonus) * 100;

  const merchantPolicy = (game.science as { getPolicy: (name: string) => { researched?: boolean; lastCalculatedBonus?: number } }).getPolicy(
    "sharkRelationsMerchants",
  );

  return {
    effectiveness: game.getDisplayValueExt(effectiveness, false, false, 0),
    sharkMerchantsBonus: game.toDisplayPercentage(
      merchantPolicy.lastCalculatedBonus ?? 0,
      0,
      false,
    ) as string,
    showSharkMerchants: !!merchantPolicy.researched,
    races: dip.races.filter((r) => r.unlocked),
  };
}

function snapshotEq(a: TradeSnapshot | null, b: TradeSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.effectiveness !== b.effectiveness ||
    a.sharkMerchantsBonus !== b.sharkMerchantsBonus ||
    a.showSharkMerchants !== b.showSharkMerchants ||
    a.races.length !== b.races.length
  ) {
    return false;
  }
  for (let i = 0; i < a.races.length; i++) {
    if (a.races[i].name !== b.races[i].name) {
      return false;
    }
  }
  return true;
}

export default function TradeTab() {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  const [collapsedOverrides, setCollapsedOverrides] = useState<Record<string, boolean>>({});

  const isCollapsed = useCallback(
    (race: RaceLike) => collapsedOverrides[race.name] ?? race.collapsed,
    [collapsedOverrides],
  );

  const setCollapsed = useCallback((raceName: string, collapsed: boolean) => {
    setCollapsedOverrides((prev) => ({ ...prev, [raceName]: collapsed }));
  }, []);

  const expandAll = useCallback(() => {
    if (!snap) {
      return;
    }
    const next: Record<string, boolean> = {};
    for (const race of snap.races) {
      next[race.name] = false;
    }
    setCollapsedOverrides(next);
  }, [snap]);

  const collapseAll = useCallback(() => {
    if (!snap) {
      return;
    }
    const next: Record<string, boolean> = {};
    for (const race of snap.races) {
      next[race.name] = true;
    }
    setCollapsedOverrides(next);
  }, [snap]);

  const racePanels = useMemo(() => {
    if (!snap) {
      return null;
    }
    return snap.races.map((race) => (
      <RacePanel
        key={race.name}
        raceName={race.name}
        collapsed={isCollapsed(race)}
        onToggle={(c) => setCollapsed(race.name, c)}
      />
    ));
  }, [snap, isCollapsed, setCollapsed]);

  if (!snap) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="tabInner Trade">
        <div className="expandAllBar" style={{ float: "left" }}>
          <span>{t("trade.effectiveness", [snap.effectiveness])}</span>
          {snap.showSharkMerchants && (
            <>
              <br />
              <span>{t("trade.effectiveness.sharkRelationsMerchants", [snap.sharkMerchantsBonus])}</span>
            </>
          )}
        </div>
        <div className="expandAllBar">
          <a href="#" onClick={expandAll}>{t("common.expand.all")}</a>
          <span>{" | "}</span>
          <a href="#" onClick={collapseAll}>{t("common.collapse.all")}</a>
        </div>
        <div className="clear" />

        {racePanels}

        <div style={{ marginBottom: "15px" }} />
        <SendExplorersButton />
      </div>
    </TooltipProvider>
  );
}
